<div class="min-h-screen bg-base-100 pb-16">
  <!-- Banner Header -->
  <div class="bg-base-200/60 border-b border-base-300 py-8 mb-8">
    <div class="container mx-auto px-4">
      <div class="daisy-breadcrumbs text-sm mb-2 text-base-content/70">
        <ul>
          <li><a routerLink="/">Home</a></li>
          <li><a routerLink="/courses">Courses</a></li>
          <li>{{ editModeEnabled ? 'Edit Course' : 'Create Course' }}</li>
        </ul>
      </div>
      <div class="flex items-center gap-3">
        <h1 class="text-3xl font-extrabold tracking-tight text-base-content">
          {{ editModeEnabled ? 'Edit Course' : 'Create New Course' }}
        </h1>
        @if (editModeEnabled && courseStatus) {
          <span
            class="daisy-badge font-bold text-xs shadow-sm uppercase tracking-wider"
            [ngClass]="{
              'daisy-badge-success text-white': isPublished,
              'daisy-badge-warning text-white': courseStatus === 'UNDER_REVIEW',
              'daisy-badge-info text-white':
                !isPublished && courseStatus !== 'UNDER_REVIEW',
              'daisy-badge-ghost': courseStatus === 'ARCHIVED',
            }"
          >
            {{ courseStatus }}
          </span>
        }
      </div>
      <p class="text-sm text-base-content/70 mt-1">
        {{
          currentSection === 1
            ? 'Step 1: Basic Information & Settings'
            : 'Step 2: Structured Curriculum Builder'
        }}
      </p>
    </div>
  </div>

  <div class="container mx-auto px-4 mb-8">
    <black-stepper [section]="currentSection"></black-stepper>
  </div>

  <!-- STEP 1: Basic & Information -->
  @if (currentSection === 1) {
    <div class="container mx-auto px-4">
      <div class="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div class="lg:col-span-2">
          <form [formGroup]="createCourseForm" id="step1" class="space-y-8">
            <!-- Basic Information Card -->
            <div
              class="daisy-card bg-base-100 border border-base-300 shadow-sm p-6"
              formGroupName="basic"
            >
              <h2
                class="text-xl font-bold text-base-content mb-4 border-b border-base-200 pb-2 flex items-center gap-2"
              >
                <i class="fa-solid fa-circle-info text-primary"></i> Basic
                Information
              </h2>
              <div class="space-y-4">
                <div>
                  <label class="daisy-label">
                    <span class="daisy-label-text font-semibold"
                      >Course Title <span class="text-error">*</span></span
                    >
                  </label>
                  <input
                    formControlName="title"
                    type="text"
                    placeholder="e.g. Complete Combined Mathematics Bootcamp"
                    class="daisy-input daisy-input-bordered w-full"
                    required
                  />
                </div>
                <div>
                  <label class="daisy-label">
                    <span class="daisy-label-text font-semibold"
                      >Course Description
                      <span class="text-error">*</span></span
                    >
                  </label>
                  <textarea
                    formControlName="description"
                    rows="6"
                    placeholder="Detailed explanation of what the course covers..."
                    class="daisy-textarea daisy-textarea-bordered w-full"
                  ></textarea>
                </div>
                <div>
                  <label class="daisy-label flex items-center gap-2">
                    <span class="daisy-label-text font-semibold"
                      >Description Summary
                      <span class="text-error">*</span></span
                    >
                    <black-tooltip
                      >A short teaser summary displayed on course cards and
                      catalog</black-tooltip
                    >
                  </label>
                  <textarea
                    formControlName="summary"
                    rows="3"
                    placeholder="Brief 1-2 sentence overview..."
                    class="daisy-textarea daisy-textarea-bordered w-full"
                  ></textarea>
                </div>
              </div>
            </div>

            <!-- Grade, Subject & Additional Information -->
            <div
              class="daisy-card bg-base-100 border border-base-300 shadow-sm p-6"
              formGroupName="information"
            >
              <h2
                class="text-xl font-bold text-base-content mb-4 border-b border-base-200 pb-2 flex items-center gap-2"
              >
                <i class="fa-solid fa-graduation-cap text-primary"></i>
                Curriculum Classification & Details
              </h2>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <!-- Target Audience / Grade -->
                <div class="md:col-span-2">
                  <label class="daisy-label flex items-center gap-2">
                    <span class="daisy-label-text font-semibold"
                      >Grade / Target Audience
                      <span class="text-error">*</span></span
                    >
                    <black-tooltip
                      >Select the intended student level for this
                      course</black-tooltip
                    >
                  </label>
                  <select
                    formControlName="targetAudience"
                    class="daisy-select daisy-select-bordered w-full"
                  >
                    <option value="" disabled selected>
                      Select target audience / grade
                    </option>
                    @for (option of targetAudienceOptions; track option) {
                      <option [value]="option">{{ option }}</option>
                    }
                    <option value="Other">Other (specify below)</option>
                  </select>
                </div>

                @if (
                  formControls.information.targetAudience.value === 'Other'
                ) {
                  <div class="md:col-span-2">
                    <label class="daisy-label">
                      <span class="daisy-label-text font-semibold"
                        >Specify Target Audience</span
                      >
                    </label>
                    <input
                      type="text"
                      class="daisy-input daisy-input-bordered w-full"
                      formControlName="targetAudienceOther"
                      placeholder="e.g. Adult Learners, Working Professionals"
                    />
                  </div>
                }

                <!-- Subject -->
                <div class="md:col-span-2">
                  <label class="daisy-label flex items-center gap-2">
                    <span class="daisy-label-text font-semibold"
                      >Subject <span class="text-error">*</span></span
                    >
                    <black-tooltip
                      >Academic or skill subject area covered</black-tooltip
                    >
                  </label>
                  @if (subjectOptions.length > 0) {
                    <select
                      formControlName="subject"
                      class="daisy-select daisy-select-bordered w-full"
                    >
                      <option value="" disabled selected>
                        Select a subject
                      </option>
                      @for (option of subjectOptions; track option) {
                        <option [value]="option">{{ option }}</option>
                      }
                      <option value="Other">Other (specify below)</option>
                    </select>
                  } @else {
                    <input
                      formControlName="subject"
                      type="text"
                      placeholder="e.g. Mathematics, Science, ICT"
                      class="daisy-input daisy-input-bordered w-full"
                    />
                  }
                </div>

                @if (formControls.information.subject.value === 'Other') {
                  <div class="md:col-span-2">
                    <label class="daisy-label">
                      <span class="daisy-label-text font-semibold"
                        >Specify Subject</span
                      >
                    </label>
                    <input
                      type="text"
                      class="daisy-input daisy-input-bordered w-full"
                      formControlName="subjectOther"
                      placeholder="e.g. Robotics, Astronomy"
                    />
                  </div>
                }

                <!-- Start Date -->
                <div>
                  <label class="daisy-label flex items-center gap-2">
                    <span class="daisy-label-text font-semibold"
                      >Start / Release Date
                      <span class="text-error">*</span></span
                    >
                    <black-tooltip
                      >Scheduled date when this course is
                      accessible</black-tooltip
                    >
                  </label>
                  <input
                    formControlName="startDate"
                    type="date"
                    class="daisy-input daisy-input-bordered w-full"
                    required
                  />
                </div>

                <!-- Medium -->
                <div>
                  <label class="daisy-label">
                    <span class="daisy-label-text font-semibold"
                      >Medium of Instruction
                      <span class="text-error">*</span></span
                    >
                  </label>
                  <select
                    formControlName="medium"
                    class="daisy-select daisy-select-bordered w-full"
                  >
                    <option value="ENGLISH">English</option>
                    <option value="TAMIL">Tamil</option>
                    <option value="SINHALA">Sinhala</option>
                  </select>
                </div>
              </div>

              <!-- Requirements (Optional) and Coverage (Optional) -->
              <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                <!-- Requirements -->
                <div formArrayName="requirements" class="space-y-2">
                  <label class="daisy-label flex items-center gap-2">
                    <span class="daisy-label-text font-semibold"
                      >Requirements
                      <span class="text-xs font-normal text-base-content/60"
                        >(Optional)</span
                      ></span
                    >
                    <black-tooltip
                      >Prerequisites students should meet before
                      starting</black-tooltip
                    >
                  </label>
                  @for (
                    item of requirementsFormControls;
                    track item;
                    let i = $index
                  ) {
                    <div class="flex items-center gap-2 mb-2">
                      <input
                        [formControl]="item"
                        type="text"
                        placeholder="e.g. Basic algebra knowledge"
                        class="daisy-input daisy-input-bordered w-full text-sm"
                      />
                      @if (requirementsFormControls.length > 1) {
                        <button
                          type="button"
                          (click)="removeRequirementInput(i)"
                          class="daisy-btn daisy-btn-ghost daisy-btn-sm text-error"
                          title="Remove"
                        >
                          <i class="fa-solid fa-trash-can"></i>
                        </button>
                      }
                    </div>
                  }
                  <button
                    type="button"
                    (click)="addMoreRequirementInputs()"
                    class="daisy-btn daisy-btn-xs daisy-btn-outline daisy-btn-primary"
                  >
                    <i class="fa-solid fa-plus mr-1"></i> Add Requirement
                  </button>
                </div>

                <!-- Coverage / Outcomes -->
                <div formArrayName="coverage" class="space-y-2">
                  <label class="daisy-label flex items-center gap-2">
                    <span class="daisy-label-text font-semibold"
                      >Learning Outcomes
                      <span class="text-xs font-normal text-base-content/60"
                        >(Optional)</span
                      ></span
                    >
                    <black-tooltip
                      >Key competencies students will gain from this
                      course</black-tooltip
                    >
                  </label>
                  @for (
                    item of coverageFormControls;
                    track item;
                    let i = $index
                  ) {
                    <div class="flex items-center gap-2 mb-2">
                      <input
                        [formControl]="item"
                        type="text"
                        placeholder="e.g. Solve advanced calculus problems"
                        class="daisy-input daisy-input-bordered w-full text-sm"
                      />
                      @if (coverageFormControls.length > 1) {
                        <button
                          type="button"
                          (click)="removeCoverageInput(i)"
                          class="daisy-btn daisy-btn-ghost daisy-btn-sm text-error"
                          title="Remove"
                        >
                          <i class="fa-solid fa-trash-can"></i>
                        </button>
                      }
                    </div>
                  }
                  <button
                    type="button"
                    (click)="addMoreCoverageInputs()"
                    class="daisy-btn daisy-btn-xs daisy-btn-outline daisy-btn-primary"
                  >
                    <i class="fa-solid fa-plus mr-1"></i> Add Outcome
                  </button>
                </div>
              </div>

              <!-- Total Duration (Optional) -->
              <div class="mt-6">
                <label class="daisy-label flex items-center gap-2">
                  <span class="daisy-label-text font-semibold"
                    >Estimated Course Duration
                    <span class="text-xs font-normal text-base-content/60"
                      >(Optional)</span
                    ></span
                  >
                  <black-tooltip
                    >Approximate video hours or completion time</black-tooltip
                  >
                </label>
                <div class="grid grid-cols-2 gap-4">
                  <div class="flex items-center gap-2">
                    <input
                      formControlName="totalDurationHour"
                      type="number"
                      placeholder="0"
                      min="0"
                      max="120"
                      class="daisy-input daisy-input-bordered w-full"
                    />
                    <span class="text-sm font-medium text-base-content/70"
                      >Hours</span
                    >
                  </div>
                  <div class="flex items-center gap-2">
                    <input
                      formControlName="totalDurationMinutes"
                      type="number"
                      placeholder="0"
                      min="0"
                      max="59"
                      class="daisy-input daisy-input-bordered w-full"
                    />
                    <span class="text-sm font-medium text-base-content/70"
                      >Minutes</span
                    >
                  </div>
                </div>
              </div>
            </div>

            <!-- Course Settings & Pricing in LKR -->
            <div
              class="daisy-card bg-base-100 border border-base-300 shadow-sm p-6"
              formGroupName="settings"
            >
              <h2
                class="text-xl font-bold text-base-content mb-4 border-b border-base-200 pb-2 flex items-center gap-2"
              >
                <i class="fa-solid fa-money-bill-wave text-primary"></i> Course
                Pricing & Banner
              </h2>
              <div class="space-y-4">
                <div>
                  <label class="daisy-label flex items-center gap-2">
                    <span class="daisy-label-text font-semibold"
                      >Course Tuition Fee (LKR)</span
                    >
                    <black-tooltip
                      >Course fee charged in Sri Lankan Rupees (LKR). Set to 0
                      for Free course.</black-tooltip
                    >
                  </label>
                  <div class="daisy-join w-full">
                    <span
                      class="daisy-btn daisy-join-item no-animation bg-base-200 font-bold text-sm"
                      >LKR</span
                    >
                    <input
                      formControlName="price"
                      type="number"
                      min="0"
                      placeholder="0"
                      class="daisy-input daisy-input-bordered daisy-join-item flex-1"
                    />
                  </div>
                  <span class="text-xs text-base-content/60 mt-1 block"
                    >Enter 0 to make this course free for all students.</span
                  >
                </div>

                <!-- Installment Options -->
                <div
                  class="border border-base-300 rounded-xl p-4 bg-base-200/40 space-y-3"
                  formGroupName="installmentOptions"
                >
                  <div class="flex items-center justify-between">
                    <div>
                      <span
                        class="font-semibold text-sm text-base-content flex items-center gap-2"
                      >
                        <i class="fa-solid fa-calendar-days text-primary"></i>
                        Allow Installment Payments
                      </span>
                      <p class="text-xs text-base-content/60 mt-0.5">
                        Let students split the tuition fee into multiple
                        scheduled payments
                      </p>
                    </div>
                    <input
                      type="checkbox"
                      formControlName="enabled"
                      class="daisy-toggle daisy-toggle-primary"
                    />
                  </div>

                  @if (
                    createCourseForm.controls.settings.controls
                      .installmentOptions.controls.enabled.value
                  ) {
                    <div
                      class="pt-3 border-t border-base-300 grid grid-cols-1 sm:grid-cols-2 gap-4"
                    >
                      <div>
                        <label class="daisy-label py-1">
                          <span class="daisy-label-text text-xs font-medium"
                            >Number of Installments</span
                          >
                        </label>
                        <select
                          formControlName="numberOfInstallments"
                          class="daisy-select daisy-select-bordered daisy-select-sm w-full"
                        >
                          <option [ngValue]="2">2 Installments</option>
                          <option [ngValue]="3">3 Installments</option>
                          <option [ngValue]="4">4 Installments</option>
                          <option [ngValue]="6">6 Installments</option>
                        </select>
                      </div>

                      <div>
                        <label class="daisy-label py-1">
                          <span class="daisy-label-text text-xs font-medium"
                            >Interval Between Payments</span
                          >
                        </label>
                        <select
                          formControlName="intervalDays"
                          class="daisy-select daisy-select-bordered daisy-select-sm w-full"
                        >
                          <option [ngValue]="14">
                            Every 2 Weeks (14 Days)
                          </option>
                          <option [ngValue]="30">Monthly (30 Days)</option>
                          <option [ngValue]="60">
                            Every 2 Months (60 Days)
                          </option>
                        </select>
                      </div>

                      @if (installmentPreview) {
                        <div
                          class="col-span-full daisy-alert daisy-alert-info py-2 px-3 text-xs flex items-center gap-2"
                        >
                          <i class="fa-solid fa-circle-info"></i>
                          <span
                            ><strong>Preview:</strong> Students pay
                            {{ installmentPreview }}.</span
                          >
                        </div>
                      }
                    </div>
                  }
                </div>

                <!-- Visibility / Public vs Unlisted -->
                <div
                  class="border border-base-300 rounded-xl p-4 bg-base-200/40"
                >
                  <div class="flex items-center justify-between">
                    <div>
                      <span
                        class="font-semibold text-sm text-base-content flex items-center gap-2"
                      >
                        <i class="fa-solid fa-globe text-primary"></i>
                        Catalog Visibility
                      </span>
                      <p class="text-xs text-base-content/60 mt-0.5">
                        {{
                          createCourseForm.get('settings.isPublic')?.value
                            ? 'Public: Listed on the website catalog and search'
                            : 'Unlisted (Private): Hidden from catalog, accessible only via direct link or batch'
                        }}
                      </p>
                    </div>
                    <label class="daisy-swap daisy-swap-rotate">
                      <input
                        type="checkbox"
                        formControlName="isPublic"
                        class="daisy-toggle daisy-toggle-primary"
                      />
                    </label>
                  </div>
                </div>

                <div>
                  <label class="daisy-label flex items-center gap-2">
                    <span class="daisy-label-text font-semibold"
                      >Course Banner</span
                    >
                    <black-tooltip
                      >Recommended 4:3 aspect ratio image for card and catalog
                      preview</black-tooltip
                    >
                  </label>
                  <file-pond
                    #myPond
                    [options]="pondOptions"
                    (onprocessfile)="pondHandleProcessFile($event)"
                    (onremovefile)="pondHandleRevertFile($event)"
                    [files]="pondFiles"
                  ></file-pond>
                </div>
              </div>
            </div>

            <!-- Form Actions -->
            <div class="flex items-center justify-between gap-4 pt-4">
              @if (selectedCourseId) {
                <a
                  class="daisy-btn daisy-btn-outline"
                  [routerLink]="['/courses', selectedCourseId]"
                  target="_blank"
                >
                  <i class="fa-solid fa-eye mr-2"></i> Preview
                </a>
              } @else {
                <div></div>
              }
              <button
                type="button"
                (click)="submit()"
                class="daisy-btn daisy-btn-primary gap-2"
              >
                <span>Next Step: Course Curriculum</span>
                <i class="fa-solid fa-arrow-right"></i>
              </button>
            </div>
          </form>
        </div>

        <!-- Right Sidebar Tips -->
        <div class="lg:col-span-1">
          <div
            class="daisy-card bg-base-200/50 border border-base-300 p-6 sticky top-24 space-y-4"
          >
            <h3
              class="text-lg font-bold text-base-content flex items-center gap-2"
            >
              <i class="fa-solid fa-lightbulb text-warning"></i> Creator
              Guidelines
            </h3>
            <ul class="space-y-3 text-sm text-base-content/80">
              <li class="flex items-start gap-2">
                <i class="fa-solid fa-check text-success mt-0.5"></i>
                <span
                  >Choose an accurate Grade and Subject so students can discover
                  your course.</span
                >
              </li>
              <li class="flex items-start gap-2">
                <i class="fa-solid fa-check text-success mt-0.5"></i>
                <span
                  >Pricing is in Sri Lankan Rupees (LKR). Free courses are
                  accessible to all verified students.</span
                >
              </li>
              <li class="flex items-start gap-2">
                <i class="fa-solid fa-check text-success mt-0.5"></i>
                <span
                  >Requirements and duration are optional but help students
                  decide if the course fits their schedule.</span
                >
              </li>
              <li class="flex items-start gap-2">
                <i class="fa-solid fa-check text-success mt-0.5"></i>
                <span
                  >In Step 2, you can structure lessons into modules and mark
                  introductory lessons as <strong>Free Preview</strong>.</span
                >
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  }

  <!-- STEP 2: LMS Curriculum & Lesson Builder -->
  @if (currentSection === 2) {
    <div class="container mx-auto px-4">
      <div
        class="grid grid-cols-1 gap-8"
        [ngClass]="isMediaCollapsed ? 'lg:grid-cols-1' : 'lg:grid-cols-3'"
      >
        <div
          [ngClass]="isMediaCollapsed ? 'lg:col-span-1' : 'lg:col-span-2'"
          class="space-y-6"
        >
          <!-- Curriculum Header -->
          <div
            class="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-5 bg-base-200/60 border border-base-300 rounded-2xl"
          >
            <div>
              <h2
                class="text-xl font-extrabold text-base-content flex items-center gap-2"
              >
                <i class="fa-solid fa-layer-group text-primary"></i> Course
                Curriculum Builder
              </h2>
              <p class="text-xs sm:text-sm text-base-content/70 mt-1">
                {{ courseItems.length }}
                {{ courseItems.length === 1 ? 'Module' : 'Modules' }} &bull;
                {{ getTotalLessons() }}
                {{ getTotalLessons() === 1 ? 'Lesson' : 'Lessons' }} &bull;
                Organize topics and attach learning resources
              </p>
            </div>

            <div class="flex items-center gap-2">
              <button
                type="button"
                (click)="toggleMediaCollapse()"
                class="daisy-btn daisy-btn-sm daisy-btn-outline gap-2 !outline-none focus:!outline-none focus-visible:!outline-none focus:!ring-0"
                [title]="
                  isMediaCollapsed
                    ? 'Expand media library'
                    : 'Collapse media library'
                "
              >
                <i
                  class="fa-solid"
                  [ngClass]="
                    isMediaCollapsed
                      ? 'fa-photo-film text-primary'
                      : 'fa-angles-right'
                  "
                ></i>
                <span>{{
                  isMediaCollapsed ? 'Show Media' : 'Hide Media'
                }}</span>
              </button>

              <!-- Add Module Modal Trigger -->
              <black-ui-modal>
                <button
                  *blackModalTrigger="let toggle = toggle"
                  (click)="toggle()"
                  class="daisy-btn daisy-btn-primary daisy-btn-sm gap-2 shadow-sm"
                >
                  <i class="fa-solid fa-plus"></i>
                  <span>Add Module</span>
                </button>
                <div
                  *blackModalContent="let close = close"
                  class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
                  (click)="close()"
                >
                  <div
                    class="bg-base-100 text-base-content border border-base-300 rounded-2xl shadow-2xl max-w-md w-full p-6 space-y-4"
                    (click)="$event.stopPropagation()"
                  >
                    <h3 class="text-lg font-bold flex items-center gap-2">
                      <i class="fa-solid fa-folder-plus text-primary"></i>
                      Create Course Module
                    </h3>
                    <div>
                      <label class="daisy-label">
                        <span class="daisy-label-text font-semibold"
                          >Module Title <span class="text-error">*</span></span
                        >
                      </label>
                      <input
                        [formControl]="topicTitleControl"
                        type="text"
                        placeholder="e.g. Module 1: Fundamental Concepts & Overview"
                        class="daisy-input daisy-input-bordered w-full"
                        required
                      />
                      <p class="text-xs text-base-content/60 mt-1.5">
                        Group related lessons into a cohesive chapter or unit.
                      </p>
                    </div>
                    <div class="flex items-center justify-end gap-2 pt-2">
                      <button
                        type="button"
                        class="daisy-btn daisy-btn-ghost daisy-btn-sm"
                        (click)="close()"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        class="daisy-btn daisy-btn-primary daisy-btn-sm"
                        (click)="addTopic(close)"
                      >
                        Create Module
                      </button>
                    </div>
                  </div>
                </div>
              </black-ui-modal>
            </div>
          </div>

          <!-- Modules List -->
          @if (courseItems.length > 0) {
            <div class="space-y-4">
              @for (module of courseItems; track module.id; let mIdx = $index) {
                <div
                  class="daisy-card bg-base-100 border border-base-300 shadow-sm overflow-hidden"
                >
                  <!-- Module Header -->
                  <div
                    class="p-4 bg-base-200/40 border-b border-base-200 flex items-center justify-between gap-4"
                  >
                    <div class="flex items-center gap-3">
                      <span
                        class="daisy-badge daisy-badge-neutral text-xs font-bold px-2 py-3"
                      >
                        Module {{ mIdx + 1 }}
                      </span>
                      <h3 class="font-bold text-base text-base-content">
                        {{ module.title }}
                      </h3>
                      <span class="text-xs text-base-content/60"
                        >({{ module.contents.length || 0 }} lessons)</span
                      >
                    </div>

                    <div class="flex items-center gap-2">
                      <!-- Add Lesson Modal Trigger -->
                      <black-ui-modal>
                        <button
                          *blackModalTrigger="let toggle = toggle"
                          (click)="openAddLesson(module.id, toggle)"
                          class="daisy-btn daisy-btn-outline daisy-btn-primary daisy-btn-xs gap-1"
                        >
                          <i class="fa-solid fa-plus text-[10px]"></i> Add
                          Lesson
                        </button>
                        <div
                          *blackModalContent="let close = close"
                          class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
                          (click)="close()"
                        >
                          <div
                            class="bg-base-100 text-base-content border border-base-300 rounded-2xl shadow-2xl max-w-md w-full p-6 space-y-4"
                            (click)="$event.stopPropagation()"
                          >
                            <h3
                              class="text-lg font-bold flex items-center gap-2"
                            >
                              <i
                                class="fa-solid fa-circle-play text-primary"
                              ></i>
                              Add Lesson to Module {{ mIdx + 1 }}
                            </h3>

                            <div>
                              <label class="daisy-label">
                                <span class="daisy-label-text font-semibold"
                                  >Lesson Title
                                  <span class="text-error">*</span></span
                                >
                              </label>
                              <input
                                [formControl]="lessonTitleControl"
                                type="text"
                                placeholder="e.g. 1.1 Introduction to Core Concepts & Principles"
                                class="daisy-input daisy-input-bordered w-full"
                                required
                              />
                            </div>

                            <div>
                              <label class="daisy-label">
                                <span class="daisy-label-text font-semibold"
                                  >Primary Material Type</span
                                >
                              </label>
                              <select
                                [formControl]="lessonTypeControl"
                                class="daisy-select daisy-select-bordered w-full"
                              >
                                <option value="video">
                                  Video Lecture (MP4, YouTube, Vimeo)
                                </option>
                                <option value="document">
                                  Study Notes & Reading (PDF, Presentation)
                                </option>
                                <option value="link">
                                  External Web Resource / Article
                                </option>
                              </select>
                            </div>

                            <!-- Lesson Content Source Options -->
                            <div>
                              <label class="daisy-label py-1">
                                <span class="daisy-label-text font-semibold"
                                  >Lesson Content Source</span
                                >
                              </label>
                              <div
                                class="grid grid-cols-4 gap-1.5 p-1.5 bg-base-200/80 rounded-xl text-xs font-semibold text-center mb-4"
                              >
                                <button
                                  type="button"
                                  class="py-2 px-2.5 rounded-lg transition-all flex flex-col items-center gap-1.5 !outline-none focus:!outline-none focus-visible:!outline-none focus:!ring-0"
                                  [ngClass]="
                                    lessonSourceMode === 'file'
                                      ? 'bg-base-100 text-primary shadow-xs font-bold'
                                      : 'text-base-content/70 hover:text-base-content hover:bg-base-100/40'
                                  "
                                  (click)="setLessonSourceMode('file')"
                                >
                                  <i
                                    class="fa-solid fa-cloud-arrow-up text-sm"
                                  ></i>
                                  <span>Upload</span>
                                </button>
                                <button
                                  type="button"
                                  class="py-2 px-2.5 rounded-lg transition-all flex flex-col items-center gap-1.5 !outline-none focus:!outline-none focus-visible:!outline-none focus:!ring-0"
                                  [ngClass]="
                                    lessonSourceMode === 'url'
                                      ? 'bg-base-100 text-primary shadow-xs font-bold'
                                      : 'text-base-content/70 hover:text-base-content hover:bg-base-100/40'
                                  "
                                  (click)="setLessonSourceMode('url')"
                                >
                                  <i class="fa-solid fa-link text-sm"></i>
                                  <span>Web URL</span>
                                </button>
                                <button
                                  type="button"
                                  class="py-2 px-2.5 rounded-lg transition-all flex flex-col items-center gap-1.5 !outline-none focus:!outline-none focus-visible:!outline-none focus:!ring-0"
                                  [ngClass]="
                                    lessonSourceMode === 'library'
                                      ? 'bg-base-100 text-primary shadow-xs font-bold'
                                      : 'text-base-content/70 hover:text-base-content hover:bg-base-100/40'
                                  "
                                  (click)="setLessonSourceMode('library')"
                                >
                                  <i class="fa-solid fa-photo-film text-sm"></i>
                                  <span>Media Lib</span>
                                </button>
                                <button
                                  type="button"
                                  class="py-2 px-2.5 rounded-lg transition-all flex flex-col items-center gap-1.5 !outline-none focus:!outline-none focus-visible:!outline-none focus:!ring-0"
                                  [ngClass]="
                                    lessonSourceMode === 'none'
                                      ? 'bg-base-100 text-primary shadow-xs font-bold'
                                      : 'text-base-content/70 hover:text-base-content hover:bg-base-100/40'
                                  "
                                  (click)="setLessonSourceMode('none')"
                                >
                                  <i class="fa-solid fa-pen-ruler text-sm"></i>
                                  <span>Outline</span>
                                </button>
                              </div>

                              <!-- Option 1: Direct File Upload -->
                              @if (lessonSourceMode === 'file') {
                                <div
                                  class="border border-dashed border-base-300 rounded-xl p-4 bg-base-100 text-center"
                                >
                                  @if (!lessonSelectedFile) {
                                    <div
                                      class="flex flex-col items-center justify-center gap-2"
                                    >
                                      <i
                                        class="fa-solid fa-file-arrow-up text-3xl text-primary/70"
                                      ></i>
                                      <div class="text-xs text-base-content/70">
                                        Drag & drop or browse a video or
                                        document from your device
                                      </div>
                                      <div
                                        class="text-[11px] text-base-content/50"
                                      >
                                        Supported formats: MP4, MKV, MOV, PDF,
                                        DOCX, PPTX
                                      </div>
                                      <label
                                        class="daisy-btn daisy-btn-primary daisy-btn-xs gap-1 cursor-pointer mt-1"
                                      >
                                        <i class="fa-solid fa-folder-open"></i>
                                        <span>Browse Files</span>
                                        <input
                                          type="file"
                                          class="hidden"
                                          accept="video/*,.pdf,.doc,.docx,.ppt,.pptx"
                                          (change)="onLessonFilePicked($event)"
                                        />
                                      </label>
                                    </div>
                                  } @else {
                                    <div class="space-y-2">
                                      @if (lessonUploadSession) {
                                        <black-file-upload
                                          [uploadSession]="lessonUploadSession"
                                        ></black-file-upload>
                                      } @else if (isLessonUploading) {
                                        <div
                                          class="flex items-center gap-3 p-3 bg-base-200/50 rounded-xl text-left"
                                        >
                                          <span
                                            class="daisy-loading daisy-loading-spinner daisy-loading-sm text-primary shrink-0"
                                          ></span>
                                          <div class="min-w-0">
                                            <div
                                              class="text-xs font-semibold text-base-content truncate"
                                            >
                                              {{ lessonSelectedFile.name }}
                                            </div>
                                            <div
                                              class="text-[11px] text-base-content/60"
                                            >
                                              Initializing upload session...
                                            </div>
                                          </div>
                                        </div>
                                      }
                                      <div class="flex justify-end">
                                        <button
                                          type="button"
                                          class="daisy-btn daisy-btn-ghost daisy-btn-xs text-error gap-1 hover:bg-error/10"
                                          (click)="removeLessonFile()"
                                        >
                                          <i
                                            class="fa-solid fa-trash-can text-xs"
                                          ></i>
                                          <span>Remove file</span>
                                        </button>
                                      </div>
                                    </div>
                                  }
                                </div>
                              }

                              <!-- Option 2: Direct URL / External Link -->
                              @if (lessonSourceMode === 'url') {
                                <div class="space-y-3">
                                  <!-- Type Selector Pills -->
                                  <div>
                                    <label
                                      class="text-xs font-semibold text-base-content/70 block mb-1.5"
                                      >Resource Type</label
                                    >
                                    <div
                                      class="grid grid-cols-3 gap-2 bg-base-200/60 p-1 rounded-lg"
                                    >
                                      <button
                                        type="button"
                                        class="py-1.5 px-2 rounded-md text-xs font-medium transition-all flex items-center justify-center gap-1.5 !outline-none focus:!outline-none"
                                        [ngClass]="
                                          lessonTypeControl.value === 'video'
                                            ? 'bg-base-100 text-primary shadow-xs font-bold'
                                            : 'text-base-content/70 hover:text-base-content'
                                        "
                                        (click)="setLessonType('video')"
                                      >
                                        <i
                                          class="fa-solid fa-circle-play text-xs"
                                        ></i>
                                        <span>Video</span>
                                      </button>
                                      <button
                                        type="button"
                                        class="py-1.5 px-2 rounded-md text-xs font-medium transition-all flex items-center justify-center gap-1.5 !outline-none focus:!outline-none"
                                        [ngClass]="
                                          lessonTypeControl.value === 'document'
                                            ? 'bg-base-100 text-primary shadow-xs font-bold'
                                            : 'text-base-content/70 hover:text-base-content'
                                        "
                                        (click)="setLessonType('document')"
                                      >
                                        <i
                                          class="fa-solid fa-file-lines text-xs"
                                        ></i>
                                        <span>Document</span>
                                      </button>
                                      <button
                                        type="button"
                                        class="py-1.5 px-2 rounded-md text-xs font-medium transition-all flex items-center justify-center gap-1.5 !outline-none focus:!outline-none"
                                        [ngClass]="
                                          lessonTypeControl.value === 'link'
                                            ? 'bg-base-100 text-primary shadow-xs font-bold'
                                            : 'text-base-content/70 hover:text-base-content'
                                        "
                                        (click)="setLessonType('link')"
                                      >
                                        <i
                                          class="fa-solid fa-arrow-up-right-from-square text-xs"
                                        ></i>
                                        <span>Web Link</span>
                                      </button>
                                    </div>
                                  </div>

                                  <div class="relative">
                                    <div
                                      class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-base-content/50"
                                    >
                                      <i class="fa-solid fa-link text-xs"></i>
                                    </div>
                                    <input
                                      [formControl]="lessonUrlControl"
                                      (input)="onLessonUrlInput()"
                                      type="url"
                                      [placeholder]="
                                        lessonTypeControl.value === 'video'
                                          ? 'e.g. https://www.youtube.com/watch?v=... or https://vimeo.com/...'
                                          : lessonTypeControl.value ===
                                              'document'
                                            ? 'e.g. https://example.com/slides.pdf'
                                            : 'e.g. https://github.com/... or https://notion.so/...'
                                      "
                                      class="daisy-input daisy-input-bordered daisy-input-sm w-full pl-9"
                                    />
                                  </div>
                                  <span
                                    class="text-[11px] text-base-content/60 block"
                                  >
                                    @if (lessonTypeControl.value === 'video') {
                                      Streamable video from YouTube, Vimeo,
                                      Google Drive, or direct MP4/HLS link.
                                    } @else if (
                                      lessonTypeControl.value === 'document'
                                    ) {
                                      Hosted PDF, slides, or document file for
                                      inline reading.
                                    } @else {
                                      External web page, tool, article, or
                                      documentation repository.
                                    }
                                  </span>
                                </div>
                              }

                              <!-- Option 3: Media Library Dropdown -->
                              @if (lessonSourceMode === 'library') {
                                <div class="space-y-1">
                                  <select
                                    [formControl]="lessonResourceControl"
                                    class="daisy-select daisy-select-bordered daisy-select-sm w-full"
                                  >
                                    <option value="" disabled selected>
                                      Select from uploaded course media files...
                                    </option>
                                    @for (
                                      res of (resourceItems$ | async) ?? [];
                                      track res._id
                                    ) {
                                      <option [value]="res._id">
                                        {{ res.name }}
                                      </option>
                                    }
                                  </select>
                                  <span
                                    class="text-[11px] text-base-content/60 block"
                                    >Attach a video or document previously
                                    uploaded to this course's media files.</span
                                  >
                                </div>
                              }

                              <!-- Option 4: No Media / Outline Only -->
                              @if (lessonSourceMode === 'none') {
                                <div
                                  class="p-3 bg-base-200/40 rounded-xl text-center text-xs text-base-content/70"
                                >
                                  <i
                                    class="fa-solid fa-lightbulb text-warning mr-1"
                                  ></i>
                                  Draft syllabus placeholder. You can attach
                                  video lectures or notes anytime before or
                                  after publishing.
                                </div>
                              }
                            </div>

                            <!-- Free Preview Toggle -->
                            <div
                              class="daisy-form-control bg-base-200/50 p-3 rounded-xl"
                            >
                              <label
                                class="daisy-label cursor-pointer justify-between py-0"
                              >
                                <div class="flex flex-col">
                                  <span
                                    class="daisy-label-text font-bold flex items-center gap-1.5 text-xs"
                                  >
                                    <i
                                      class="fa-solid fa-eye text-success text-xs"
                                    ></i>
                                    Free Preview Lesson
                                  </span>
                                  <span class="text-[11px] text-base-content/60"
                                    >Allow prospective students to preview this
                                    lesson for free before enrolling</span
                                  >
                                </div>
                                <input
                                  type="checkbox"
                                  [formControl]="lessonFreePreviewControl"
                                  class="daisy-toggle daisy-toggle-success daisy-toggle-sm"
                                />
                              </label>
                            </div>

                            <div
                              class="flex items-center justify-end gap-2 pt-2 border-t border-base-200"
                            >
                              <button
                                type="button"
                                class="daisy-btn daisy-btn-ghost daisy-btn-sm"
                                (click)="close()"
                              >
                                Cancel
                              </button>
                              <button
                                type="button"
                                class="daisy-btn daisy-btn-primary daisy-btn-sm"
                                [disabled]="
                                  isLessonUploading ||
                                  (lessonSourceMode === 'file' &&
                                    lessonSelectedFile &&
                                    !lessonUploadSuccess)
                                "
                                (click)="addLesson(close)"
                              >
                                @if (isLessonUploading) {
                                  <span
                                    class="daisy-loading daisy-loading-spinner daisy-loading-xs"
                                  ></span>
                                  <span>Uploading...</span>
                                } @else {
                                  <span>Add Lesson to Module</span>
                                }
                              </button>
                            </div>
                          </div>
                        </div>
                      </black-ui-modal>

                      <!-- Delete Module Button -->
                      <button
                        type="button"
                        (click)="deleteTopic(module.id)"
                        class="daisy-btn daisy-btn-ghost daisy-btn-xs text-error daisy-tooltip daisy-tooltip-left"
                        data-tip="Delete Module"
                      >
                        <i class="fa-solid fa-trash-can"></i>
                      </button>
                    </div>
                  </div>

                  <!-- Lessons List Inside Module -->
                  <div class="p-3">
                    @if (module.contents && module.contents.length > 0) {
                      <div class="divide-y divide-base-200">
                        @for (
                          lesson of module.contents;
                          track lesson.id;
                          let lIdx = $index
                        ) {
                          <div
                            class="py-2.5 px-3 flex items-center justify-between gap-3 hover:bg-base-200/30 rounded-lg transition-colors"
                          >
                            <div class="flex items-center gap-3 min-w-0">
                              <span
                                class="text-xs font-mono text-base-content/40 w-6"
                              >
                                {{ mIdx + 1 }}.{{ lIdx + 1 }}
                              </span>

                              @if (lesson.type === 'document') {
                                <i
                                  class="fa-solid fa-file-pdf text-error text-base"
                                ></i>
                              } @else if (lesson.type === 'link') {
                                <i
                                  class="fa-solid fa-link text-info text-base"
                                ></i>
                              } @else {
                                <i
                                  class="fa-solid fa-circle-play text-primary text-base"
                                ></i>
                              }

                              <div class="truncate">
                                <div
                                  class="text-sm font-semibold text-base-content truncate"
                                >
                                  {{ lesson.title }}
                                </div>
                                <span class="text-[11px] text-base-content/50">
                                  @if (lesson.url) {
                                    <span
                                      class="text-info flex items-center gap-1"
                                    >
                                      <i
                                        class="fa-solid fa-link text-[10px]"
                                      ></i>
                                      <span
                                        class="truncate max-w-[200px] inline-block"
                                        >{{ lesson.url }}</span
                                      >
                                    </span>
                                  } @else if (lesson.resourceId) {
                                    <span
                                      class="text-success flex items-center gap-1"
                                    >
                                      <i
                                        class="fa-solid fa-file-circle-check text-[10px]"
                                      ></i>
                                      File Attached
                                    </span>
                                  } @else {
                                    <span class="italic text-base-content/40"
                                      >Syllabus outline (no media
                                      attached)</span
                                    >
                                  }
                                </span>
                              </div>
                            </div>

                            <div class="flex items-center gap-3 shrink-0">
                              <!-- Free Preview Pill / Button -->
                              <button
                                type="button"
                                (click)="toggleFreePreview(module.id, lesson)"
                                class="daisy-badge daisy-badge-sm cursor-pointer transition-all gap-1 text-[10px] font-bold"
                                [ngClass]="
                                  lesson.isFreePreview
                                    ? 'daisy-badge-success text-white'
                                    : 'daisy-badge-ghost text-base-content/50'
                                "
                                [title]="
                                  lesson.isFreePreview
                                    ? 'Click to make Enrolled Only'
                                    : 'Click to enable Free Preview'
                                "
                              >
                                <i
                                  class="fa-solid"
                                  [ngClass]="
                                    lesson.isFreePreview ? 'fa-eye' : 'fa-lock'
                                  "
                                ></i>
                                <span>{{
                                  lesson.isFreePreview
                                    ? 'Free Preview'
                                    : 'Enrolled Only'
                                }}</span>
                              </button>

                              <!-- Delete Lesson Button -->
                              <button
                                type="button"
                                (click)="deleteResource(module.id, lesson.id)"
                                class="daisy-btn daisy-btn-ghost daisy-btn-xs text-error/70 hover:text-error daisy-btn-square"
                                title="Remove Lesson"
                              >
                                <i class="fa-solid fa-xmark"></i>
                              </button>
                            </div>
                          </div>
                        }
                      </div>
                    } @else {
                      <div
                        class="p-6 text-center text-xs text-base-content/50 border border-dashed border-base-300 rounded-xl bg-base-100"
                      >
                        No lessons added yet. Click
                        <strong>'+ Add Lesson'</strong> above to add video
                        lectures, reading materials, or external resources.
                      </div>
                    }
                  </div>
                </div>
              }
            </div>
          } @else {
            <div
              class="p-12 text-center bg-base-100 border border-dashed border-base-300 rounded-2xl space-y-3"
            >
              <div
                class="w-14 h-14 bg-primary/10 text-primary rounded-xl flex items-center justify-center mx-auto text-2xl"
              >
                <i class="fa-solid fa-list-check"></i>
              </div>
              <h3 class="text-lg font-bold text-base-content">
                Build Your Course Curriculum
              </h3>
              <p
                class="text-xs sm:text-sm text-base-content/70 max-w-sm mx-auto"
              >
                Organize your course into structured modules or chapters, then
                add video lectures, reading notes, and exercises to each module.
              </p>
            </div>
          }

          <!-- Bottom Actions -->
          <div
            class="flex flex-wrap items-center justify-between gap-4 pt-6 border-t border-base-300"
          >
            <button
              type="button"
              class="daisy-btn daisy-btn-ghost daisy-btn-sm gap-2"
              (click)="changeCurrentSection()"
            >
              <i class="fa-solid fa-arrow-left"></i>
              <span>Back to Course Settings</span>
            </button>
            <div class="flex items-center gap-3">
              <button
                type="button"
                (click)="syncCourseContentData()"
                class="daisy-btn daisy-btn-outline daisy-btn-sm"
              >
                {{
                  (isSyncingContents$ | async)
                    ? 'Saving...'
                    : isPublished
                      ? 'Save Changes'
                      : 'Save Curriculum Draft'
                }}
              </button>
              <button
                type="button"
                (click)="syncAndExit()"
                class="daisy-btn daisy-btn-primary daisy-btn-sm gap-2"
              >
                <span>{{
                  isPublished ? 'Save & Exit' : 'Continue to Publish'
                }}</span>
                <i
                  class="fa-solid"
                  [class.fa-check]="isPublished"
                  [class.fa-arrow-right]="!isPublished"
                ></i>
              </button>
            </div>
          </div>
        </div>

        <!-- Right Sidebar Media Uploader (Collapsible) -->
        @if (!isMediaCollapsed) {
          <div class="lg:col-span-1">
            <div class="sticky top-24 space-y-4">
              <div
                class="daisy-card bg-base-100 border border-base-300 shadow-sm p-4"
              >
                <div class="flex items-center justify-between gap-2 mb-3">
                  <h3
                    class="font-bold text-base text-base-content flex items-center gap-2"
                  >
                    <i class="fa-solid fa-photo-film text-primary"></i> Course
                    Media Files
                  </h3>
                  <button
                    type="button"
                    (click)="toggleMediaCollapse()"
                    class="daisy-btn daisy-btn-ghost daisy-btn-xs daisy-btn-circle text-base-content/60 hover:text-base-content !outline-none focus:!outline-none focus-visible:!outline-none focus:!ring-0"
                    title="Collapse Media Sidebar"
                  >
                    <i class="fa-solid fa-xmark text-xs"></i>
                  </button>
                </div>
                <p class="text-xs text-base-content/70 mb-3">
                  Optional: Upload or manage files in bulk. You can also upload
                  files or attach external web links directly when creating each
                  lesson.
                </p>
                <black-course-upload
                  [selectedCourseId$]="selectedCourseId$"
                ></black-course-upload>
              </div>
            </div>
          </div>
        }
      </div>
    </div>
  }
</div>
